import { App } from "@capacitor/app";
import { config } from "../config";
import { isRealNativePlatform } from "./platform";
import { CohesiveShareAuth } from "./shareAuth";

/**
 * Mirrors the current Auth0 access token (plus the API base URL the bundle was
 * built with) into the shared Keychain for the iOS Share Extension.
 *
 * Why a custom Keychain plugin and not `@capacitor/preferences` with a `group`:
 * the Preferences plugin's `group` option is only a key *prefix* inside
 * `UserDefaults.standard` — it never touches an App Group suite, so a share
 * extension can't read it. Do not "simplify" back to Preferences.
 *
 * Only the ~24h access token is mirrored, never the refresh token; every app
 * foreground re-mirrors a fresh token, and logout clears the mirror.
 */

let appStateListenerInstalled = false;
let warnedOnce = false;
let latestGetToken: (() => Promise<string>) | null = null;

async function push(getToken: () => Promise<string>): Promise<void> {
  try {
    const accessToken = await getToken();
    await CohesiveShareAuth.setCredentials({
      accessToken,
      apiBaseUrl: config.hosts.api,
    });
  } catch (err) {
    if (!warnedOnce) {
      warnedOnce = true;
      console.warn("shareAuthMirror: failed to mirror credentials", err);
    }
  }
}

/** Mirror now and on every app foreground. No-op off native. */
export function mirrorShareAuth(getToken: () => Promise<string>): void {
  if (!isRealNativePlatform()) return;
  latestGetToken = getToken;
  void push(getToken);
  if (!appStateListenerInstalled) {
    appStateListenerInstalled = true;
    void App.addListener("appStateChange", ({ isActive }) => {
      if (isActive && latestGetToken) void push(latestGetToken);
    });
  }
}

/** Remove mirrored credentials (call on logout). No-op off native. */
export async function clearShareAuth(): Promise<void> {
  if (!isRealNativePlatform()) return;
  latestGetToken = null;
  try {
    await CohesiveShareAuth.clearCredentials();
  } catch {
    // Missing native plugin must never break logout.
  }
}
